import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Essay } from 'src/app/model/essay';
import { EssayProtocol } from 'src/app/model/essayProtocol';
import { ExecutionStatus } from 'src/app/model/execution';
import { Sensor } from 'src/app/model/sensor';
import { SensorData } from 'src/app/model/sensorData';
import { AttachmentThermalService } from 'src/app/services/attachmentThermal.service';
import { SnackBarService } from 'src/app/services/snackBar.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { ArrayUtils } from 'src/app/utils/arrayUtils';
import { ExcelUtils } from 'src/app/utils/excelUtils';
import { excelColumnsFromSensorData, keepSensorColumn, matchExcelColumnsToSensors } from 'src/app/utils/excelSensorMatching';
import { ExecutionEditMassiveUploadComponent } from './execution-edit-massive-upload.component';


export interface DialogDataBottomSheetUpload {
  idStatusExecution: number;
  essays: Essay[];
  essay: EssayProtocol;
  essayIndex: number;
  variableIndex: number;
}

@Component({
    selector: 'app-execution-edit-bottom-sheet-upload',
    templateUrl: './execution-edit-bottom-sheet-upload.component.html',
    standalone: false,
})
export class ExecutionEditBottomSheetUploadComponent {

  @ViewChild('uploadExcelMassive') uploadExcelMassiveInput: ElementRef;
  @ViewChild('uploadAutomaticMassiveFile') uploadAutomaticMassiveInput: ElementRef;

  private destroy$ = new Subject<void>();

  constructor(
    private attachmentThermalService: AttachmentThermalService,
    @Inject(MAT_BOTTOM_SHEET_DATA) public data: DialogDataBottomSheetUpload,
    private bottomSheetRef: MatBottomSheetRef<ExecutionEditBottomSheetUploadComponent>,
    public snackBarService: SnackBarService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private spinnerService: SpinnerService) { }

  disableUploadPhotoButton(): boolean {
    const allowedStatuses = [ExecutionStatus.EN_EJECUCION];

    return !allowedStatuses.includes(this.data.idStatusExecution);
  }

  disableUploadExcelButton(): boolean {
    const allowedStatuses = [ExecutionStatus.EN_EJECUCION];

    return !allowedStatuses.includes(this.data.idStatusExecution);
  }

  disableUploadOriginalButton(): boolean {
    const allowedStatuses = [ExecutionStatus.EN_EJECUCION];

    return !allowedStatuses.includes(this.data.idStatusExecution);
  }

  openMassiveUploadPhotoDialog(): void {
    this.openMassiveUploadGenericDialog('photo');
  }

  openMassiveUploadExcelDialog(): void {
    this.openMassiveUploadGenericDialog('excel');
  }

  openMassiveUploadPrimaryDataDialog(): void {
    this.openMassiveUploadGenericDialog('primary');
  }

  openMassiveUploadGenericDialog(type: string): void {
    const sensors = this.data.essay.sensors;

    const dialogRef = this.dialog.open(ExecutionEditMassiveUploadComponent, {
      minWidth: '50%',
      maxHeight: '95vh',
      data: { sensors, type }
    });

    dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result: Sensor[]) => {
      if (result != null) {
        this.data.essay.sensors = result;
        this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.uploadMassive.ok') as string);

        this.bottomSheetRef.dismiss();
      }
    });
  }

  onExcelMassiveUploadSensor(event): void {
    const variableSelected = this.data.essay;

    const file = event.target.files[0] as File;

    this.spinnerService.show();

    this.uploadExcelMassiveInput.nativeElement.value = '';

    if (file.name.endsWith('.pdf')) {
      this.spinnerService.show();

      this.attachmentThermalService.convertPdfToCsv(file, variableSelected.idVariable).pipe(takeUntil(this.destroy$)).subscribe(res => {
        res.name = file.name;
        this.uploadMassiveDataExcel(res as File);
      });
    } else {
      this.uploadMassiveDataExcel(file);
    }
  }

  uploadAutomaticMassive(event): void {
    this.spinnerService.show();

    const fileList: FileList = event.target.files as FileList;
    const files: File[] = Array.from(fileList);

    this.uploadAutomaticMassiveInput.nativeElement.value = '';

    if (ArrayUtils.isEmpty(files)) {
      this.spinnerService.hide();
      return;
    }

    // Antes: el aviso verde se lanzaba aqui mismo, sincrono, sin haber esperado a una
    // sola subida y pasara lo que pasara; y el cierre se programaba con un setTimeout
    // de 200 ms por fichero. Al cerrar, la pantalla se recarga (reloadPage renavega a
    // la ruta) y relee la ejecucion mientras las subidas siguen en vuelo, asi que la
    // tabla mostraba solo las que habian dado tiempo. El operador leia "correcto" y se
    // encontraba ficheros sin subir. Ahora se espera a que terminen y se informa de lo
    // que de verdad ha pasado.
    void this.generatePromisesMassiveAutomatic(files).then((fallos: string[]) => {
      this.spinnerService.hide();

      if (ArrayUtils.isEmpty(fallos)) {
        this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.uploadMassiveAutomatic.ok') as string);
      } else {
        this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadMassiveAutomatic.error',
          { fallos: fallos.length, total: files.length, ficheros: fallos.join(', ') }) as string);
      }

      this.bottomSheetRef.dismiss({ action: 'reloadPage' });
    });
  }

  /**
   * Sube los ficheros y devuelve los nombres de los que NO han podido subirse.
   *
   * La version anterior lanzaba generatePromiseMassiveAutomatic sin esperarla y
   * resolvia acto seguido, y ademas hacia Promise.all sobre un Map -cuyos elementos
   * son pares [clave, valor] y no promesas-, con lo que resolvia siempre al instante
   * y sin subir nada. Aqui se espera de una en una: todas las sondas de una ejecucion
   * escriben sobre la misma fila de execution y lanzarlas a la vez solo produce
   * contencion en la base.
   */
  private async generatePromisesMassiveAutomatic(files: File[]): Promise<string[]> {
    const fallos: string[] = [];

    const expandidos: File[] = [];

    files.forEach(f => {
      const fileName = f.name;

      if (fileName.includes(',')) {
        const fileNameNoExt = fileName.substring(0, fileName.lastIndexOf('.'));
        const ext = fileName.substring(fileName.lastIndexOf('.'));

        fileNameNoExt.split(',').map(s => s.trim()).forEach(s => expandidos.push(
          new File([f], s.concat(ext), { type: f.type })));
      } else {
        expandidos.push(f);
      }
    });

    for (const file of expandidos) {
      try {
        const subido = await this.generatePromiseMassiveAutomatic(file);

        if (!subido) {
          fallos.push(file.name);
        }
      } catch (err) {
        console.error(err);
        fallos.push(file.name);
      }
    }

    return fallos;
  }

  /** Resuelve true si el fichero se ha subido, false si no. Nunca se queda colgada. */
  private generatePromiseMassiveAutomatic(file: File): Promise<boolean> {
    const variableSelected = this.data.essay;
    const sensors = variableSelected.sensors;

    const fileName: string = file.name;
    let eqName = fileName.substring(0, file.name.indexOf(' '));

    if (eqName === '') {
      eqName = fileName.substring(0, fileName.lastIndexOf('.'));
    }

    return new Promise<boolean>((resolve) => {
      const possibleIndex = +eqName;

      if (!isNaN(possibleIndex) && typeof sensors[possibleIndex - 1] !== 'undefined') {
        const indexSensor = possibleIndex;
        const sensor = sensors[indexSensor - 1];

        this.resolvePromiseMassiveAutomatic(indexSensor, sensor, file, resolve);

        return;
      }

      for (let indexSensor = 0; indexSensor < sensors.length; indexSensor++) {
        const sensor = sensors[indexSensor];

        if (eqName.toLowerCase() === sensor.equipmentName.toLowerCase() || eqName.toLowerCase() === sensor.serialNum.toLowerCase()) {
          this.resolvePromiseMassiveAutomatic(indexSensor, sensor, file, resolve);

          return;
        }
      }

      // Ningun sensor casa con el nombre del fichero. Antes la promesa se quedaba sin
      // resolver y colgaba la subida entera en silencio.
      resolve(false);
    });
  }

  private uploadMassiveDataExcel(file: File) {
    const variableSelected = this.data.essay;

    const allSensors = variableSelected.sensors;

    const idVariable = variableSelected.idVariable;

    // QLOD-290: se leen TODAS las columnas y se empareja antes de subir nada. Antes se leian solo las
    // primeras N (N = sondas) y, si las cabeceras no casaban, se asignaban por posicion sin avisar.
    ExcelUtils.excelToSensorData(file, this.attachmentThermalService, idVariable, true, allSensors.length, true)
      .then((sensorsData: SensorData[]) => {

      if (sensorsData == null || sensorsData.length === 0) {
        this.spinnerService.hide();

        this.snackBarService.sendError(this.translate.instant('executionEdit.essays.form.uploadExcel.error.generic') as string);

        this.bottomSheetRef.dismiss({ action: 'updateGraphByIndex' });

        return;
      }

      const match = matchExcelColumnsToSensors(excelColumnsFromSensorData(sensorsData), allSensors);

      if (match.ok === false) {
        this.spinnerService.hide();
        this.snackBarService.sendError(match.error);

        return;
      }

      let itsFirtsCall = true;

      const promises = match.assignments.map(({ sensorIndex, position }) => new Promise<void>((resolve, reject) => {
        const sensor = allSensors[sensorIndex];

        let data = sensorsData.filter(item => item.posEquipment === position).map(item => ({ ...item }) as SensorData);

        data.forEach(item => {
          item.serialNum = sensor.serialNum;
          item.equipmentName = sensor.equipmentName;
          item.idEquipment = sensor.idEquipment;
          item.multiChannelName = sensor.multiChannelName;
          item.idMultichannel = sensor.idMultichannel;
          delete item.posEquipment;
        });

        data = data.filter(item => item.date != null && !isNaN(item.date.getTime()));

        const map = ArrayUtils.groupBy(data, (item: SensorData) => item.idVariable) as Map<number, SensorData[]>;

        if (map.size > 1) {
          data = map.get(variableSelected.idVariable);
        }

        this.uploadExcelFromMassive(sensorIndex, file, data, resolve, reject, itsFirtsCall);
        itsFirtsCall = false;
      }));

      Promise.all(promises).then(() => {
        this.spinnerService.hide();
        this.snackBarService.sendSuccess(this.translate.instant('executionEdit.essays.form.uploadExcel.ok') as string);

        this.bottomSheetRef.dismiss({ action: 'updateGraphByIndex' });
      }).catch(err => {
        console.error(err);
        this.snackBarService.sendError(err as string);
        this.spinnerService.hide();

        this.bottomSheetRef.dismiss({ action: 'updateGraphByIndex' });
      });

    }).catch(err => {
      console.error(err);
      this.snackBarService.sendError(err as string);
      this.spinnerService.hide();
    });
  }

  private uploadExcelFromMassive(sensorIndex: number, file: File, data: SensorData[], callback: () => void,
    errorCallback: () => void, itsFirtsCall: boolean) {
    const sensor = this.getSensor(sensorIndex);

    this.attachmentThermalService.uploadExcelToSensor(sensor.id, file, data).pipe(takeUntil(this.destroy$)).subscribe((item: number) => {
      sensor.idExcel = item;
      sensor.data = data;

      this.bottomSheetRef.dismiss({ action: 'updateSensor', data: { sensorIndex, sensor, itsFirtsCall } });

      callback();
    }, () => errorCallback());
  }

  private uploadPromiseMassiveDataExcel(sensorIndex: number, file: File, callback: (value: boolean) => void) {
    const variableSelected = this.data.essay;

    const sensor = this.getSensor(sensorIndex);

    ExcelUtils.excelToSensorData(file, this.attachmentThermalService, variableSelected.idVariable, false, 1, true).then(data => {
      // QLOD-290: con un fichero de varias columnas se cogia siempre la primera.
      data = keepSensorColumn(data, sensor);

      data.forEach(item => {
        item.serialNum = sensor.serialNum;
        item.equipmentName = sensor.equipmentName;
        item.idEquipment = sensor.idEquipment;
      });

      const map = ArrayUtils.groupBy(data, (item: SensorData) => item.idVariable);

      if (map.size > 1) {
        data = map.get(variableSelected.idVariable) as SensorData[];
      }

      data.forEach(item => delete item.posEquipment);

      data = data.filter(item => item.date != null && !isNaN(item.date.getTime()));

      this.attachmentThermalService.uploadExcelToSensor(sensor.id, file, data).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: number) => {
          sensor.idExcel = res;
          sensor.data = data;

          this.bottomSheetRef.dismiss({ action: 'updateSensor', data: { sensorIndex, sensor, itsFirtsCall: true } });

          callback(true);
        },
        error: (err: unknown) => {
          console.error(err);
          callback(false);
        }
      });
    }).catch((err: unknown) => {
      // Sin este catch, un excel ilegible dejaba la promesa sin resolver y colgaba la
      // subida entera.
      console.error(err);
      callback(false);
    });
  }

  private resolvePromiseMassiveAutomatic(sensorIndex: number, sensor: Sensor, file: File,
    resolve: ((value: boolean) => void)): void {

    const fileName: string = file.name;

    const variableSelected = this.data.essay;

    const excelExts = ['.xls', '.xlsx', '.csv', '.pdf'];

    const ext = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();

    const fallo = (err: unknown) => {
      console.error(err);
      resolve(false);
    };

    if (excelExts.indexOf(ext) > -1) {

      if (file.name.endsWith('.pdf')) {
        this.spinnerService.show();

        this.attachmentThermalService.convertPdfToCsv(file, variableSelected.idVariable).pipe(takeUntil(this.destroy$))
          .subscribe({
            // Antes aqui se pasaba resolve(), o sea, se INVOCABA la funcion: la promesa
            // se daba por cumplida antes de subir nada y lo que llegaba al callback era
            // undefined.
            next: res => {
              res.name = file.name;
              this.uploadPromiseMassiveDataExcel(sensorIndex, res as File, resolve);
            },
            error: fallo
          });
      } else {
        this.uploadPromiseMassiveDataExcel(sensorIndex, file, resolve);
      }

    } else if (file.type.includes('image/')) {
      this.attachmentThermalService.uploadPhotoToSensor(sensor.id, file).pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: number) => {
            sensor.idPhoto = res;

            this.bottomSheetRef.dismiss({ action: 'updateSensorPhoto', data: { sensorIndex, sensor, itsFirtsCall: true } });

            resolve(true);
          },
          error: fallo
        });
    } else {
      this.attachmentThermalService.uploadPrimaryFileToSensor(sensor.id, file).pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: number) => {
            sensor.idPrimaryDataFile = res;

            this.bottomSheetRef.dismiss({ action: 'updateSensorPrimary', data: { sensorIndex, sensor, itsFirtsCall: true } });

            resolve(true);
          },
          error: fallo
        });
    }
  }

  private getSensor(sensorIndex: number): Sensor {
    return this.data.essay.sensors[sensorIndex];
  }

}
